import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Award, Clock, Shield, Cpu, Users, HeartHandshake, 
  ArrowRight, Check, ExternalLink, Zap, Star
} from 'lucide-react';
import { WHY_INKFLY, BRAND } from '../data/mock';

const icons = [Award, Clock, Shield, Cpu, Users, HeartHandshake];

const WhyInkflyPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Why Choose Us
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-8">
              The Inkfly{' '}
              <span className="gold-gradient-text">Advantage</span>
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed mb-6">
              With 13+ years of experience, 5000+ successful events, and a legacy as Chennai&apos;s first 
              photo booth company since 2012, Inkfly Photo Booth is one of India&apos;s leading photo booth brands.
            </p>
            <p className="text-gray-500 text-lg leading-relaxed">
              We specialize in premium, tech-driven photo booth experiences for corporate events, 
              brand activations, weddings, and large-scale celebrations across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_INKFLY.map((item, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-[#00BFFF]/30 hover:shadow-lg transition-all group"
                >
                  <div className="w-16 h-16 rounded-2xl bg-[#00BFFF]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-[#00BFFF]" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-20 bg-[#00BFFF]/5 border-y border-[#00BFFF]/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '2012', label: 'Established' },
              { value: '13+', label: 'Years Experience' },
              { value: '5000+', label: 'Events Delivered' },
              { value: 'Pan-India', label: 'Presence' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="font-display text-4xl md:text-5xl font-bold gold-gradient-text mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600 text-sm uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Our Offerings
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              Premium Products & Experiences
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Core Products */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gray-50 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#00BFFF]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-900">Premium Products</h3>
              </div>
              <ul className="space-y-3">
                {['iMIRROR', 'AirBooth', 'Retro Booth', 'Vintage Booth', 'Vogue Video Booth', 'GIF Booth'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#00BFFF]" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* High-End Experiences */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gray-50 border border-gray-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#00BFFF]/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#00BFFF]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-900">High-End Experiences</h3>
              </div>
              <ul className="space-y-3">
                {['Time Freeze Booth', 'Photo Mosaic Wall', 'Mr. Grapher'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#00BFFF]" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              The Difference
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              What Sets Us Apart
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Inkfly */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-white border-2 border-[#00BFFF]/30 shadow-lg"
              >
                <h3 className="font-display text-2xl font-bold text-[#00BFFF] mb-6">
                  Inkfly Photo Booth
                </h3>
                <ul className="space-y-4">
                  {[
                    '13+ years of proven expertise',
                    'Premium build quality',
                    'AI-powered technology',
                    'Dedicated support team',
                    'Custom branding options',
                    'Pan-India operations',
                    '5000+ events delivered',
                    'Continuous innovation'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00BFFF] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Others */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-gray-100 border border-gray-200"
              >
                <h3 className="font-display text-2xl font-bold text-gray-400 mb-6">
                  Typical Competitors
                </h3>
                <ul className="space-y-4">
                  {[
                    'Limited experience',
                    'Basic equipment',
                    'Standard features only',
                    'Minimal support',
                    'Template-based designs',
                    'Local operations only',
                    'Limited portfolio',
                    'Following trends'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full border border-gray-300 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-400">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="text-gray-600 text-xl md:text-2xl leading-relaxed italic">
              &quot;Inkfly Photo Booth combines innovation, experience, and flawless execution to create 
              memorable, share-worthy experiences that elevate every event.&quot;
            </p>
          </motion.div>
        </div>
      </section>

      {/* Own a Booth Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Business Opportunity
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Start Your Photo Booth Business
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Looking to start or upgrade your photo booth business? Explore our professional 
              booth ownership solutions at PhotoboothGuru.
            </p>
            <a
              href={BRAND.photoboothGuruUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex"
            >
              Explore Ownership Options <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-gray-600 mb-8">
              Join thousands of satisfied clients who chose Inkfly for their memorable events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Book Your Booth <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/gallery" className="btn-secondary">
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyInkflyPage;
